import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-upload-data',
  imports: [CommonModule, FormsModule],
  templateUrl: './upload-data.component.html',
  styleUrl: './upload-data.component.scss'
})
export class UploadDataComponent {
  @Input() participantIdentifier: string = '';
  @Input() isVisible: boolean = false;
  @Output() uploadCompleted = new EventEmitter<void>();
  @Output() uploadCancelled = new EventEmitter<void>();

  isUploading = false;
  uploadError = '';
  showRenameInput = false;
  newParticipantIdentifier = '';
  uploadStep: 'initial' | 'uploading' | 'rename' | 'success' | 'error' = 'initial';

  private readonly API_URL = `${environment.baseApiUrl}/uploadParticipantData`;

  constructor(
    private http: HttpClient,
    private dbService: NgxIndexedDBService,
    private oidcSecurityService: OidcSecurityService
  ) {}

  ngOnChanges() {
    if (this.isVisible && this.participantIdentifier) {
      this.resetComponent();
      this.newParticipantIdentifier = this.participantIdentifier;
    }
  }

  resetComponent() {
    this.isUploading = false;
    this.uploadError = '';
    this.showRenameInput = false;
    this.uploadStep = 'initial';
    this.newParticipantIdentifier = this.participantIdentifier;
  }

  async startUpload() {
    this.uploadStep = 'uploading';
    this.isUploading = true;
    this.uploadError = '';

    try {
      await this.performUpload();
    } catch (error: any) {
      // Check if it's a 401 Unauthorized error (token expired)
      if (error.status === 401) {
        console.log('401 Unauthorized - Token likely expired, attempting to refresh and retry...');
        try {
          // Force token refresh
          await this.refreshTokenAndRetry();
        } catch (refreshError: any) {
          console.error('Token refresh failed:', refreshError);
          this.uploadStep = 'error';
          this.uploadError = 'Authentication expired. Please log in again.';
        }
      } else if (error.error?.message?.includes('already exists')) {
        this.uploadStep = 'rename';
        this.showRenameInput = true;
      } else {
        this.uploadStep = 'error';
        this.uploadError = error.error?.message || error.message || 'Upload failed';
      }
    } finally {
      this.isUploading = false;
    }
  }

  private async refreshTokenAndRetry(): Promise<void> {
    console.log('Starting token refresh process...');
    
    // Check if user is still authenticated
    const isAuthenticated = await firstValueFrom(this.oidcSecurityService.isAuthenticated());
    
    // Get current token before refresh
    const oldToken = await firstValueFrom(this.oidcSecurityService.getAccessToken());

    // Force a token refresh by calling checkAuth
    const authResult = await firstValueFrom(this.oidcSecurityService.checkAuth());
    
    // Wait a moment for the token to be refreshed
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Get new token after refresh
    const newToken = await firstValueFrom(this.oidcSecurityService.getAccessToken());
    
    // Check authentication status again
    const newAuthStatus = await firstValueFrom(this.oidcSecurityService.isAuthenticated());
    
    // If we have a token, proceed even if isAuthenticated is false
    // This might be a timing issue or OIDC configuration problem
    if (!newToken) {
      throw new Error('Failed to obtain new token after refresh');
    }
    
    if (!newAuthStatus) {
      console.warn('Warning: isAuthenticated is still false, but we have a token. Proceeding anyway...');
    }
    
    // Retry the upload
    await this.performUpload();
  }

  private async performUpload(): Promise<void> {
    // Get the access token from OIDC
    const token = await firstValueFrom(this.oidcSecurityService.getAccessToken());
    
    if (!token) {
      throw new Error('No authentication token available. Please log in again.');
    }

    // Check if token is expired
    const tokenExpired = this.isTokenExpired(token);
    
    if (tokenExpired) {
      console.warn('Token is expired but proceeding with upload attempt...');
    }

    // Get participant data from IndexedDB
    const participant: any = await firstValueFrom(
      this.dbService.getByKey('participants', this.participantIdentifier)
    );
    
    const responses: any = await firstValueFrom(
      this.dbService.getByKey('responses', this.participantIdentifier)
    );

    if (!participant || !responses) {
      throw new Error('Participant data not found in local database');
    }

    // Prepare data for upload
    const uploadData = {
      participantIdentifier: this.newParticipantIdentifier,
      trialSequenceNumber: participant.trialSequenceNumber,
      responses: responses.taskData,
      forTesting: environment.forTesting // Use environment configuration
    };

    // Prepare headers with Authorization token
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    });

    // Make API call with authorization header
    const result = await firstValueFrom(
      this.http.post(this.API_URL, uploadData, { headers })
    );
    
    // Remove data from IndexedDB after successful upload
    await this.removeFromIndexedDB();
    
    this.uploadStep = 'success';
    
    // Auto-close after 2 seconds
    setTimeout(() => {
      this.closePopup();
    }, 2000);
  }

  async retryWithNewName() {
    if (!this.newParticipantIdentifier.trim()) {
      this.uploadError = 'Please enter a valid participant identifier';
      return;
    }

    // Reset and try upload with new name
    this.showRenameInput = false;
    await this.startUpload();
  }

  private async removeFromIndexedDB() {
    try {
      // Remove from participants store
      await firstValueFrom(
        this.dbService.delete('participants', this.participantIdentifier)
      );
      
      // Remove from responses store
      await firstValueFrom(
        this.dbService.delete('responses', this.participantIdentifier)
      );
      
      console.log(`Removed ${this.participantIdentifier} from IndexedDB`);
    } catch (error) {
      console.error('Error removing data from IndexedDB:', error);
    }
  }

  closePopup() {
    if (this.uploadStep === 'success') {
      this.uploadCompleted.emit();
    } else {
      this.uploadCancelled.emit();
    }
  }

  onBackdropClick(event: Event) {
    if (event.target === event.currentTarget && !this.isUploading) {
      this.closePopup();
    }
  }

  login() {
    this.oidcSecurityService.authorize();
  }

  // Helper function to decode JWT and check expiry
  private decodeJWT(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding JWT:', error);
      return null;
    }
  }

  private isTokenExpired(token: string): boolean {
    const decoded = this.decodeJWT(token);
    if (!decoded || !decoded.exp) {
      return true;
    }
    
    const currentTime = Math.floor(Date.now() / 1000);
    const isExpired = decoded.exp < currentTime;
    
    return isExpired;
  }
}
